import React, { memo, useEffect, useState } from 'react'
import propTypes from 'prop-types'
import AddImagePreview from '../AddImagePreview'
import Button from '../../Button'
import PaginationContainer from '../../Pagination'
import { Container } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import './styles.scss'

const SearchResults = ({
  results,
  closeModal,
  chooseImage,
  isSearchTab = false,
  setResultsTotal,
}) => {
  const [openCardModal, setOpenCardModal] = useState(false)
  const [chosenImage, setChosenImage] = useState(null)
  const [currentPaginationPage, setCurrentPaginationPage] = useState(1)
  const [paginationPages, setPaginationPages] = useState([])
  const [resultsToShow, setResultsToShow] = useState(null)

  // Filters
  const defaultSortMethod = isSearchTab ? 'score' : 'alphabet'
  const [sortMethod, setSortMethod] = useState(defaultSortMethod)
  const [showOnlyTransparent, setShowOnlyTransparent] = useState(false)

  const sortResults = (results) => {
    const resultsCopy = [...results]
    if (sortMethod === 'score') {
      return resultsCopy.sort((a, b) => b.score - a.score)
    } else if (sortMethod === 'alphabet') {
      return resultsCopy.sort((a, b) => a.name.localeCompare(b.name))
    } else if (sortMethod === 'alphabet-reversed') {
      return resultsCopy.sort((a, b) => b.name.localeCompare(a.name))
    }
    return resultsCopy
  }

  useEffect(() => {
    if (results?.images?.length > 0) {
      const length = Math.ceil(results.images.length / 40)
      let pages = [1]
      for (let i = 2; i <= length; i++) {
        pages.push(i)
      }
      setPaginationPages(pages)
      setCurrentPaginationPage(1)
      getResultsToShow()
    }
  }, [results])

  useEffect(() => {
    getResultsToShow()
  }, [currentPaginationPage, sortMethod, showOnlyTransparent, results])

  const getResultsToShow = () => {
    if (!results?.images?.length) {
      return
    }
    const sortedResults = sortResults(results.images)
    const filteredResults = showOnlyTransparent
      ? sortedResults.filter((result) => result.transparent)
      : sortedResults
    setResultsTotal(filteredResults?.length || 0)
    if (filteredResults.length <= 40) {
      setResultsToShow(filteredResults)
    } else if (filteredResults.length > 40) {
      const startIndex = currentPaginationPage * 40 - 40
      const endIndex = currentPaginationPage * 40
      setResultsToShow(filteredResults.slice(startIndex, endIndex))
    }
    const length = Math.ceil(filteredResults.length / 40)
    let pages = [1]
    for (let i = 2; i <= length; i++) {
      pages.push(i)
    }
    setPaginationPages(pages)
  }

  useEffect(() => {
    if (showOnlyTransparent) {
      setCurrentPaginationPage(1)
    }
  }, [showOnlyTransparent])

  const getPagesToDisplay = () => {
    // shows all pages in pagination if there are less than 12, but if there are more,
    // it filters out the pages that should not be shown but ellipsis shown instead.
    if (paginationPages.length < 12) {
      return paginationPages
    } else if (currentPaginationPage < 8) {
      // add ellipsis to end
      return paginationPages.filter((p) => p < 11 || p > paginationPages.length - 2)
    } else if (currentPaginationPage > paginationPages.length - 6) {
      // add ellipsis to beginning
      return paginationPages.filter(
        (p) => p > paginationPages.length - 11 || p === 1
      )
    } else if (
      currentPaginationPage > 5 &&
      currentPaginationPage <= paginationPages.length - 6
    ) {
      // add ellipsis to both sides
      return paginationPages.filter(
        (p) =>
          p === 1 ||
          p > paginationPages.length - 2 ||
          (p > currentPaginationPage - 5 && p < currentPaginationPage + 5)
      )
    }
  }

  const previewImage = (result) => {
    setOpenCardModal(true)
    setChosenImage(result)
  }

  const handleAuthorClick = (img) => {
    if (!img?.author) {
      window.alert('Kuvan tekijätieto puuttuu.')
    } else {
      navigator.clipboard.writeText(img.author)
      window.alert('Kuvan tekijätieto kopioitu leikepöydälle.')
    }
  }

  return (
    <>
      {results && results.images && (
        <Container>
          <PaginationContainer
            results={results.images}
            currentPaginationPage={currentPaginationPage}
            setCurrentPaginationPage={setCurrentPaginationPage}
            pagesToDisplay={getPagesToDisplay()}
            paginationPages={paginationPages}
            showFiltersElement={true}
            isSearchTab={isSearchTab}
            sortMethod={sortMethod}
            setSortMethod={setSortMethod}
            showOnlyTransparent={showOnlyTransparent}
            setShowOnlyTransparent={setShowOnlyTransparent}
          />
          <div className="image-results-container">
            {resultsToShow &&
              resultsToShow.map((result) => {
                return (
                  <Button
                    key={`${result.uid}-${result.type}`}
                    className="choose-image-button"
                    label={`Valitse ${result.name}.`}
                    onClick={() => previewImage(result)}
                  >
                    <img src={result.thumb} alt="" width="auto" height="auto" />
                    <div
                      className="list-image-author"
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAuthorClick(result)
                      }}
                      tabIndex="0"
                      aria-label={result.author || ''}
                      title={result.author || ''}
                      hidden={!result.author}
                    >
                      <FontAwesomeIcon icon={['fas', 'info-circle']} />
                    </div>
                    <div className="search-result-text">{result.name}</div>
                  </Button>
                )
              })}
          </div>
          <PaginationContainer
            results={resultsToShow || []}
            currentPaginationPage={currentPaginationPage}
            setCurrentPaginationPage={setCurrentPaginationPage}
            pagesToDisplay={getPagesToDisplay()}
            paginationPages={paginationPages}
            showFiltersElement={false}
          />
          {openCardModal && (
            <AddImagePreview
              openCardModal={openCardModal}
              setOpenCardModal={setOpenCardModal}
              closeModal={closeModal}
              result={chosenImage}
              chooseImage={chooseImage}
            />
          )}
        </Container>
      )}
    </>
  )
}

SearchResults.propTypes = {
  results: propTypes.instanceOf(Object).isRequired,
  chooseImage: propTypes.func.isRequired,
  closeModal: propTypes.func.isRequired,
  isSearchTab: propTypes.bool,
  setResultsTotal: propTypes.func.isRequired,
}

export default memo(SearchResults)
